/// <amd-module name="Environments/EnvironmentChecks/Components/EnvironmentChecksComponent" />
import React = require("react");

import * as Contracts from "DistributedTask/Client/Contracts/TaskAgent";

import { ChecksOverview } from "Checks/ChecksOverview/ChecksOverview";

import { addBreadcrumbs } from "Environments/Breadcrumb/EnvironmentsHubBreadcrumbService";
import { EnvironmentBaseComponent, IEnvironmentBaseComponentProperties, IEnvironmentBaseComponentState } from "Environments/EnvironmentsCommon/EnvironmentBaseComponent";
import { getEnvironmentChecksPageUrl, getCurrentEnvironmentIdFromRoute, getEnvironmentsPageUrl, getEnvironmentPageUrl } from "Environments/EnvironmentsCommon/Link";
import { EnvironmentChecksActionsCreator, EnvironmentChecksPageKey } from "Environments/EnvironmentChecks/ActionsCreators/EnvironmentChecksActionsCreator";
import { EnvironmentsSource } from "Environments/EnvironmentChecks/Source/EnvironmentsSource";
import { EnvironmentChecksStore } from "Environments/EnvironmentChecks/Stores/EnvironmentChecksStore";
import * as Resources from "Environments/EnvironmentChecks/Resources";
import { ActionCreatorManager } from "Environments/Flux/ActionCreatorManager";
import { StoreManager } from "Environments/Flux/StoreManager";

import { VssComponent, IVssComponentContext, IVssComponentProperties } from "VSS/Platform/Layout";
import { onClickFPS } from "VSS/Platform/FPS";

import { Header, TitleSize } from "VSSUI/Header";
import { Page } from "VSSUI/Page";
import { Surface, SurfaceBackground } from "VSSUI/Surface";

export interface IEnvironmentChecksComponentState extends IEnvironmentBaseComponentState {
    environment: Contracts.EnvironmentInstance | undefined;
}

export class EnvironmentChecksComponent extends EnvironmentBaseComponent<IEnvironmentBaseComponentProperties, IEnvironmentChecksComponentState> {

    public constructor(props: IVssComponentProperties, context: IVssComponentContext) {
        super(props, context);

        let environmentId = getCurrentEnvironmentIdFromRoute(context.pageContext);

        this._environmentId = environmentId;
        this.state = {
            environment: undefined,
            errorMessages: {}
        };

        if (this._environmentId) {
            this._actionCreator = ActionCreatorManager.CreateActionCreator<EnvironmentChecksActionsCreator>(EnvironmentChecksActionsCreator, this.getInstanceId(context));
            this._store = StoreManager.CreateStore<EnvironmentChecksStore>(EnvironmentChecksStore, this.getInstanceId(context));
            this._store.addChangedListener(this._onEnvironmentChecksStoreChange);
            this._actionCreator.getEnvironmentById(this.context.pageContext, this._environmentId, false);
            this._source = EnvironmentsSource.getInstance(context.pageContext);
            this._hasManagePermision = this._source.checkAdminPermission(this._environmentId.toString());
        }
    }

    public getInstanceId(context: IVssComponentContext): string {
        return "environment-checks-id-" + this.componentId;
    }

    public render(): JSX.Element {
        return (
            <Surface background={SurfaceBackground.neutral}>
                <Page className="flex-grow">
                    {this._getHeader()}
                    {this._getErrorMessage()}
                    <div className="pipelines-environment-checks page-content page-content-top">
                        {!this._getErrorMessage() && this._environmentId && this._getEnvironmentChecksOverview()}
                    </div>
                </Page>
            </Surface>
        )
    }

    private _getEnvironmentChecksOverview = (): JSX.Element => {
        return (
            <ChecksOverview
                resourceType={this._environmentResourceType}
                resourceId={this._environmentId.toString()}
                resourceName={this.state.environment ? this.state.environment.name : ""}
                hasManagePermission={this._hasManagePermision} />);
    }

    private _getErrorMessage(): React.ReactNode {
        if (this._messagesStore.getMessage(EnvironmentChecksPageKey)) {
            return (
                <div className="page-content page-content-top">
                    {this.getErrorMessageView(EnvironmentChecksPageKey)}
                </div>
            )
        }
    }

    private _getHeader(): JSX.Element {
        const description = this.state.environment ? this.state.environment.name : Resources.EnvironmentsSubtitleText
        let environmentChecksTitle: JSX.Element = (
            <Header
                title={Resources.ChecksTitleText}
                titleSize={TitleSize.Large}
                description={description}
                backButtonProps={{ onClick: this._onBackButtonClick }}
            />
        );

        return environmentChecksTitle;
    }

    private _onBackButtonClick = (): void => {
        onClickFPS(this.context.pageContext, this._getEnvironmentSummaryUrl(), true);
    }

    private _getEnvironmentSummaryUrl(): string {
        if (this._environmentId) {
            return getEnvironmentPageUrl(this.context.pageContext, this._environmentId)
        }

        return getEnvironmentsPageUrl(this.context.pageContext);
    }

    private _onEnvironmentChecksStoreChange = (): void => {
        const environment = this._store.getEnvironment();

        // Render checks breadcrumb
        this._addBreadcrumbs(environment);

        // Re-render the page using the acquired environment
        this.setState({ environment });
    }

    private _addBreadcrumbs(environment: Contracts.EnvironmentInstance): void {
        if (environment) {
            const { pageContext } = this.context;

            const environmentPageUrl = getEnvironmentPageUrl(pageContext, environment.id);
            const environmentChecksPageUrl = getEnvironmentChecksPageUrl(pageContext, environment.id);
            addBreadcrumbs(
                pageContext, 
                { name: environment.name, url: environmentPageUrl },
                { name: Resources.ChecksTitleText, url: environmentChecksPageUrl }
            );
        }
    }

    private _environmentId: number;
    private _store: EnvironmentChecksStore;
    private _actionCreator: EnvironmentChecksActionsCreator;
    private _environmentResourceType: string = "environment";
    private _hasManagePermision: boolean;
    private _source: EnvironmentsSource;
}

VssComponent.register("environmentChecksView", EnvironmentChecksComponent);